import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { sparkApi } from '@api/spark-client';
import type {
  CreatePromptDto,
  UpdatePromptDto,
  PromptListResponse,
  PromptResponse,
  PromptVersionHistoryResponse,
  PromptStatus,
} from '@/types/prompt';

const PROMPTS_BASE_URL = '/api/v1/admin/prompts';

export interface GetPromptsParams {
  status?: PromptStatus;
  search?: string;
  limit?: number;
  skip?: number;
}

export interface UpdatePromptParams {
  promptId: string;
  data: UpdatePromptDto;
}

export interface UpdatePromptStatusParams {
  promptId: string;
  status: PromptStatus;
}

export const promptsQueryKeys = {
  all: ['prompts'] as const,
  lists: () => [...promptsQueryKeys.all, 'list'] as const,
  list: (params: GetPromptsParams) => [...promptsQueryKeys.lists(), params] as const,
  details: () => [...promptsQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...promptsQueryKeys.details(), id] as const,
  versions: (id: string) => [...promptsQueryKeys.all, 'versions', id] as const,
  version: (documentId: string, versionNumber: number) =>
    [...promptsQueryKeys.all, 'version', documentId, versionNumber] as const,
};

// API functions (internal)
const getPrompts = async (params: GetPromptsParams = {}): Promise<PromptListResponse> => {
  const searchParams = new URLSearchParams();

  if (params.status) searchParams.append('status', params.status);
  if (params.search) searchParams.append('search', params.search);
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.skip) searchParams.append('skip', params.skip.toString());

  const url = searchParams.toString()
    ? `${PROMPTS_BASE_URL}/?${searchParams.toString()}`
    : `${PROMPTS_BASE_URL}/`;

  const response = await sparkApi.get<PromptListResponse>(url);
  return response.data;
};

const createPrompt = async (data: CreatePromptDto): Promise<{ message: string }> => {
  const response = await sparkApi.post<{ message: string }>(`${PROMPTS_BASE_URL}/`, data);
  return response.data;
};

const getPrompt = async (promptId: string): Promise<PromptResponse> => {
  const response = await sparkApi.get<PromptResponse>(`${PROMPTS_BASE_URL}/${promptId}`);
  return response.data;
};

const updatePrompt = async (params: UpdatePromptParams): Promise<{ message: string }> => {
  const response = await sparkApi.put<{ message: string }>(
    `${PROMPTS_BASE_URL}/${params.promptId}`,
    params.data
  );
  return response.data;
};

const updatePromptStatus = async (
  params: UpdatePromptStatusParams
): Promise<{ message: string }> => {
  const response = await sparkApi.patch<{ message: string }>(
    `${PROMPTS_BASE_URL}/${params.promptId}/status`,
    { status: params.status }
  );
  return response.data;
};

const deletePrompt = async (promptId: string): Promise<{ message: string }> => {
  const response = await sparkApi.delete<{ message: string }>(`${PROMPTS_BASE_URL}/${promptId}`);
  return response.data;
};

const getPromptVersions = async (promptId: string): Promise<PromptVersionHistoryResponse> => {
  const response = await sparkApi.get<PromptVersionHistoryResponse>(
    `${PROMPTS_BASE_URL}/${promptId}/versions`
  );
  return response.data;
};

const revertToPromptVersion = async (
  documentId: string,
  versionNumber: number
): Promise<{ message: string }> => {
  const response = await sparkApi.post<{ message: string }>(
    `${PROMPTS_BASE_URL}/${documentId}/revert/${versionNumber}`
  );
  return response.data;
};

// Query hooks
export function usePrompts(params: GetPromptsParams = {}) {
  return useQuery({
    queryKey: promptsQueryKeys.list(params),
    queryFn: () => getPrompts(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function usePrompt(promptId: string, enabled = true) {
  return useQuery({
    queryKey: promptsQueryKeys.detail(promptId),
    queryFn: () => getPrompt(promptId),
    enabled: enabled && !!promptId,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePromptVersions(promptId: string, enabled = true) {
  return useQuery({
    queryKey: promptsQueryKeys.versions(promptId),
    queryFn: () => getPromptVersions(promptId),
    enabled: enabled && !!promptId,
    staleTime: 5 * 60 * 1000,
  });
}

// Mutation hooks
export function useCreatePrompt() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('prompts');

  return useMutation({
    mutationFn: (data: CreatePromptDto) => createPrompt(data),
    onSuccess: () => {
      // Invalidate and refetch prompts list
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.lists() });
      toast.success(t('toast.createPromptSuccess'));
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || error.message;
      toast.error(t('toast.createPromptError', { message }));
    },
  });
}

export function useUpdatePrompt() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('prompts');

  return useMutation({
    mutationFn: (params: UpdatePromptParams) => updatePrompt(params),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.detail(variables.promptId) });
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.versions(variables.promptId) });
      toast.success(t('toast.updatePromptSuccess'));
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || error.message;
      toast.error(t('toast.updatePromptError', { message }));
    },
  });
}

export function useUpdatePromptStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('prompts');

  return useMutation({
    mutationFn: (params: UpdatePromptStatusParams) => updatePromptStatus(params),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.detail(variables.promptId) });
      toast.success(t('toast.updatePromptStatusSuccess'));
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || error.message;
      toast.error(t('toast.updatePromptStatusError', { message }));
    },
  });
}

export function useDeletePrompt() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('prompts');

  return useMutation({
    mutationFn: (promptId: string) => deletePrompt(promptId),
    onSuccess: () => {
      // Invalidate prompts list
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.lists() });
      toast.success(t('toast.deletePromptSuccess'));
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || error.message;
      toast.error(t('toast.deletePromptError', { message }));
    },
  });
}

export function useRevertToPromptVersion() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('prompts');

  return useMutation({
    mutationFn: ({ documentId, versionNumber }: { documentId: string; versionNumber: number }) =>
      revertToPromptVersion(documentId, versionNumber),
    onSuccess: (_, variables) => {
      // Invalidate all prompt-related queries, especially for the reverted document
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.all });
      // Also invalidate specific queries for the document that was reverted
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.detail(variables.documentId) });
      queryClient.invalidateQueries({ queryKey: promptsQueryKeys.versions(variables.documentId) });
      toast.success(t('toast.revertPromptVersionSuccess'));
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || error.message;
      toast.error(t('toast.revertPromptVersionError', { message }));
    },
  });
}
